import {
    ColDef,
    IAfterGuiAttachedParams,
    IDoesFilterPassParams,
    IFilterComp,
    IFilterParams,
    IFloatingFilterComp,
    IFloatingFilterParams,
} from '@ag-grid-community/core';
import { FilterOption } from '@/components/ui/responsive-grid/types';

export interface BranchOption {
    id: string;
    name: string;
}

export interface BranchFilterParams extends IFilterParams {
    branches: BranchOption[];
}

export function branchesFromSession(session: { scope?: { branches?: BranchOption[] } } | null | undefined): BranchOption[] {
    return session?.scope?.branches?.map((branch) => ({ id: branch.id, name: branch.name })) ?? [];
}

export function branchFilterValueMaps(branches: BranchOption[]): Record<string, Record<string, string>> {
    const map = Object.fromEntries(branches.map((branch) => [branch.id, branch.name]));
    return { branch: map, branchName: map };
}

export function branchColumnFilter(branches: BranchOption[]) {
    return {
        filter: BranchFilter,
        filterParams: { branches },
        floatingFilterComponent: BranchFloatingFilter,
    };
}

function isBranchColumn<T>(col: ColDef<T>): boolean {
    const field = String(col.field ?? '');
    return col.headerName === 'Branch' || field === 'branch' || field === 'branchName';
}

export function applyBranchColumnFilter<T>(colDefs: ColDef<T>[] | undefined, branches: BranchOption[]): ColDef<T>[] {
    return (colDefs ?? []).map((col) => (isBranchColumn(col) ? { ...col, ...branchColumnFilter(branches) } : col));
}

export class BranchFilter implements IFilterComp {
    filterParams!: BranchFilterParams;
    filterValue!: string | null;
    gui!: HTMLDivElement;
    eFilterSelect!: HTMLSelectElement;

    static getFilterOptions(params?: { branches?: BranchOption[] }): FilterOption[] {
        const branches = params?.branches ?? [];
        return [{ value: '', label: 'All Branches' }, ...branches.map((b) => ({ value: b.id, label: b.name }))];
    }

    init(params: BranchFilterParams) {
        this.filterParams = params;
        this.filterValue = null;
        this.setupGui(params);
    }

    setupGui(params: BranchFilterParams) {
        this.gui = document.createElement('div');
        this.gui.className = 'ag-filter-body-wrapper';

        const container = document.createElement('div');
        container.className = 'ag-filter-body';
        container.style.padding = '8px';

        this.eFilterSelect = document.createElement('select');
        this.eFilterSelect.style.width = '100%';
        this.eFilterSelect.style.height = '28px';
        this.eFilterSelect.className = 'ag-input-field-input ag-text-field-input';

        for (const opt of BranchFilter.getFilterOptions({ branches: params.branches })) {
            const option = document.createElement('option');
            option.value = opt.value;
            option.textContent = opt.label;
            this.eFilterSelect.appendChild(option);
        }

        this.eFilterSelect.addEventListener('change', () => {
            this.filterValue = this.eFilterSelect.value || null;
            params.filterChangedCallback();
        });

        container.appendChild(this.eFilterSelect);
        this.gui.appendChild(container);
    }

    getGui() {
        return this.gui;
    }

    doesFilterPass(params: IDoesFilterPassParams) {
        const { node } = params;
        const branchName = this.filterParams.getValue(node)?.toString();

        if (!this.filterValue) {
            return true;
        }

        // Find the branch name for the selected ID
        const selectedBranch = this.filterParams.branches.find((b) => b.id === this.filterValue);
        if (!selectedBranch) {
            return true;
        }

        return branchName === selectedBranch.name;
    }

    isFilterActive() {
        return this.filterValue != null && this.filterValue !== '';
    }

    getModel() {
        if (!this.isFilterActive()) {
            return null;
        }

        return { value: this.filterValue };
    }

    setModel(model: { value: string } | null) {
        const newValue = model == null ? null : model.value;
        this.eFilterSelect.value = newValue || '';
        this.filterValue = newValue;
    }

    afterGuiAttached(params?: IAfterGuiAttachedParams): void {
        if (!params?.suppressFocus) {
            this.eFilterSelect.focus();
        }
    }
}

export class BranchFloatingFilter implements IFloatingFilterComp {
    private eGui!: HTMLDivElement;
    private eSelect!: HTMLSelectElement;
    private params!: IFloatingFilterParams<BranchFilter>;

    init(params: IFloatingFilterParams<BranchFilter>) {
        this.params = params;
        this.eGui = document.createElement('div');
        this.eGui.style.width = '100%';
        this.eGui.style.height = '100%';
        this.eGui.style.display = 'flex';
        this.eGui.style.alignItems = 'center';

        this.eSelect = document.createElement('select');
        this.eSelect.style.width = '100%';
        this.eSelect.style.height = '24px';
        this.eSelect.style.fontSize = '12px';
        this.eSelect.className = 'ag-input-field-input ag-text-field-input';

        // Options will be populated when parentFilterInstance is available
        this.params.parentFilterInstance((instance) => {
            const branches = instance.filterParams.branches;

            for (const opt of BranchFilter.getFilterOptions({ branches })) {
                const option = document.createElement('option');
                option.value = opt.value;
                option.textContent = opt.label;
                this.eSelect.appendChild(option);
            }
        });

        this.eSelect.addEventListener('change', () => {
            this.params.parentFilterInstance((instance) => {
                instance.setModel(this.eSelect.value ? { value: this.eSelect.value } : null);
                instance.filterParams.filterChangedCallback();
            });
        });

        this.eGui.appendChild(this.eSelect);
    }

    getGui() {
        return this.eGui;
    }

    onParentModelChanged(parentModel: { value: string } | null) {
        this.eSelect.value = parentModel?.value || '';
    }

    destroy() {
        // Cleanup if needed
    }
}
